import React from 'react';
import { Area, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RevenueChart = ({ data = [], loading = false, period = 'thisMonth' }) => {
  // Omzetdoelen berekenen
  const getRevenueTargets = () => {
    const dailyTarget = 2000; // 2 medewerkers × €1000 per dag
    const weeklyTarget = 10000; // 5 werkdagen × €2000 per dag
    const monthlyTarget = 43300; // 4.33 weken × €10000 per week

    if (period === 'today' || period === 'yesterday') {
      return data.map(item => ({ ...item, target: dailyTarget }));
    } else if (period === 'thisWeek' || period === 'lastWeek') {
      return data.map(item => ({ ...item, target: weeklyTarget }));
    } else {
      return data.map(item => ({ ...item, target: monthlyTarget }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatMonth = (month, year) => {
    const monthNames = [
      'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  const formatDay = (day, month) => {
    const monthNames = [
      'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'
    ];
    return `${day} ${monthNames[month - 1]}`;
  };

  const formatWeek = (week, month) => {
    const monthNames = [
      'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'
    ];
    return `W${week} ${monthNames[month - 1]}`;
  };

  if (loading) {
    return (
      <div className="card-blue">
        <div className="card-header">
          <div style={{ height: '24px', width: '220px', background: 'var(--color-neutral-200)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
        </div>
        <div className="card-body">
          <div style={{ height: '256px', background: 'var(--color-neutral-100)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
        </div>
      </div>
    );
  }

  const chartDataWithTargets = getRevenueTargets();

  const chartData = chartDataWithTargets.map(item => {
    let label;
    if (item.day) {
      label = formatDay(item.day, item.month);
    } else if (item.week) {
      label = formatWeek(item.week, item.month);
    } else {
      label = formatMonth(item.month, item.year);
    }

    return {
      month: label,
      revenue: item.total_revenue || 0,
      target: item.target
    };
  });

  const getChartTitle = () => {
    if (period === 'today' || period === 'yesterday') {
      return 'Dagelijkse Omzet Trend (14 dagen)';
    } else if (period === 'thisWeek' || period === 'lastWeek') {
      return 'Wekelijkse Omzet Trend (3 maanden)';
    } else {
      return 'Maandelijkse Omzet Trend';
    }
  };

  const getYAxisDomain = () => {
    if (period === 'today' || period === 'yesterday') {
      return [0, 2000];
    } else if (period === 'thisWeek' || period === 'lastWeek') {
      return [0, 10000];
    } else {
      return [0, 43300];
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-neutral-200)'
        }}>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--color-neutral-900)',
            marginBottom: 'var(--space-2)'
          }}>{label}</p>
          {payload.map((item, index) => (
            <p key={index} style={{
              fontSize: 'var(--text-sm)',
              color: item.dataKey === 'revenue' ? 'var(--color-blue-600)' : 'var(--color-error)',
              fontWeight: item.dataKey === 'revenue' ? 'var(--font-medium)' : 'var(--font-normal)'
            }}>
              {item.dataKey === 'revenue' ? 'Omzet' : 'Doel'}: {formatCurrency(item.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card-blue">
      <div className="card-header">
        <h3 className="card-title">{getChartTitle()}</h3>
      </div>
      <div className="card-body">
        <div style={{ height: '256px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-blue-500)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-blue-500)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
                angle={-45}
                textAnchor="end"
                height={60}
                axisLine={{ stroke: 'var(--color-neutral-300)' }}
              />
              <YAxis
                domain={getYAxisDomain()}
                tickFormatter={formatCurrency}
                tick={{ fontSize: 12, fill: 'var(--color-neutral-600)' }}
                axisLine={{ stroke: 'var(--color-neutral-300)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="var(--color-blue-500)"
                strokeWidth={2}
                fill="url(#blueGradient)"
                name="revenue"
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="var(--color-error)"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="target"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
